import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import { useGlobalStore } from "~/stores/store-provider";
import {
  FaArrowLeft,
  FaCheck,
  FaTimes,
  FaDatabase,
  FaInfoCircle,
  FaLink,
} from "react-icons/fa";
import { SiPostgresql } from "react-icons/si";
import { validatePostgreSQLUrl } from "~/components/database/utils/url-validator";

type PostgreSQLFormData = {
  name: string;
  url: string;
};

const PostgreSQLConnection = () => {
  const navigate = useNavigate();
  const { addConnection } = useGlobalStore((state) => state);
  const timersRef = useRef<Set<NodeJS.Timeout>>(new Set());

  const [formData, setFormData] = useState<PostgreSQLFormData>({
    name: "",
    url: "",
  });

  const [urlError, setUrlError] = useState<boolean>(false);
  const [nameError, setNameError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<"success" | "error">("success");

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(timer => clearTimeout(timer));
    };
  }, []);

  const handleNameChange = (value: string) => {
    setFormData({
      ...formData,
      name: value,
    });

    // Clear name error
    if (value.trim()) {
      setNameError("");
    }
  };

  const handleUrlChange = (value: string) => {
    setFormData({
      ...formData,
      url: value,
    });

    // Validate URL and set error if invalid
    if (value.trim() === "") {
      setUrlError(false);
    } else {
      const validatedData = validatePostgreSQLUrl(value);
      setUrlError(!validatedData.isValid);
    }
  };

  const validateForm = (): boolean => {
    let isValid = true;

    // Validate connection name
    if (!formData.name.trim()) {
      setNameError("Connection name is required");
      isValid = false;
    } else if (formData.name.length < 3 || formData.name.length > 50) {
      setNameError("Must be between 3 and 50 characters");
      isValid = false;
    }

    // Validate URL
    if (!formData.url.trim()) {
      setUrlError(true);
      isValid = false;
    } else {
      const validatedData = validatePostgreSQLUrl(formData.url);
      if (!validatedData.isValid) {
        setUrlError(true);
        isValid = false;
      }
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Parse the URL to get the connection details
      const validatedData = validatePostgreSQLUrl(formData.url).parsed;

      // Prepare connection data
      const connectionData = {
        name: formData.name,
        connection_type: "postgresql",
        ...validatedData,
      };

      await addConnection(connectionData);

      // Show success toast before navigating
      setToastType("success");
      setToastMessage("Database connected successfully!");
      setShowToast(true);

      const timer = setTimeout(() => {
        navigate("/connections");
        timersRef.current.delete(timer);
      }, 1500);
      timersRef.current.add(timer);
    } catch (error) {
      console.error("Failed to save connection:", error);

      // Show error toast
      setToastType("error");
      setToastMessage("Failed to save connection. Please try again.");
      setShowToast(true);

      const timer = setTimeout(() => {
        setShowToast(false);
        timersRef.current.delete(timer);
      }, 2000);
      timersRef.current.add(timer);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full bg-base-200/50 overflow-y-auto">
      <div className="container mx-auto px-4 py-8 max-w-2xl min-h-full">
        {/* Back Navigation */}
        <button
          onClick={() => navigate("/connections")}
          className="btn btn-ghost btn-sm gap-2 mb-6"
        >
          <FaArrowLeft className="w-3 h-3" />
          Back to Connections
        </button>

        {/* Main Card */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body p-8">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-base-200">
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <SiPostgresql className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold">
                  Connect PostgreSQL Database
                </h1>
                <p className="text-sm text-base-content/60">
                  Configure your PostgreSQL database connection
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Connection Name */}
              <div className="form-control w-full">
                <label className="label mb-2">
                  <span className="label-text font-medium">
                    Connection Name
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g., Production DB"
                  className={`input input-bordered w-full ${
                    nameError ? "input-error" : ""
                  }`}
                />
                {nameError && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {nameError}
                    </span>
                  </label>
                )}
              </div>

              {/* Database URL */}
              <div className="form-control w-full">
                <label className="label mb-2">
                  <span className="label-text font-medium">Database URL</span>
                  <div
                    className="tooltip"
                    data-tip="PostgreSQL connection string format"
                  >
                    <FaInfoCircle className="w-3 h-3 text-base-content/50" />
                  </div>
                </label>
                <label className="input input-bordered flex items-center gap-2 w-full">
                  <FaLink className="w-4 h-4 text-base-content/50" />
                  <input
                    type="text"
                    value={formData.url}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    placeholder="postgresql://username:password@host:port/database"
                    className={`grow font-mono text-sm ${
                      urlError ? "text-error" : ""
                    }`}
                  />
                </label>
                {urlError && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      Invalid connection string format
                    </span>
                  </label>
                )}
              </div>

              {/* Connection Format Help */}
              <div className="alert bg-base-200/50 border-0">
                <FaDatabase className="w-4 h-4" />
                <div className="text-sm">
                  <p className="font-medium mb-1">Connection String Format</p>
                  <code className="text-sm bg-base-300 px-2 py-1 rounded">
                    postgresql://root:pass@localhost:3306/mydb
                  </code>
                </div>
              </div>


              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-base-200">
                <button
                  type="button"
                  onClick={() => navigate("/connections")}
                  className="btn btn-ghost"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    urlError ||
                    formData.url.trim() === "" ||
                    !formData.name.trim()
                  }
                  className="btn btn-primary"
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Connecting...
                    </>
                  ) : (
                    "Save Connection"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Help Links */}
        <div className="text-center mt-6 space-y-2">
          <a
            href="https://www.postgresql.org/docs/current/libpq-connect.html#LIBPQ-CONNSTRING"
            target="_blank"
            rel="noopener noreferrer"
            className="link link-primary text-sm block"
          >
            PostgreSQL connection string documentation →
          </a>
        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div className="toast toast-bottom toast-center">
          <div className={`alert ${toastType === 'success' ? 'alert-success' : 'alert-error'}`}>
            {toastType === 'success' ? (
              <FaCheck className="w-4 h-4" />
            ) : (
              <FaTimes className="w-4 h-4" />
            )}
            <span>{toastMessage}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostgreSQLConnection;
